import React from 'react';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { translate as t } from 'hooks/useTranslations';
import { DraftManual, ReleaseNotes } from 'views/App/Portal/Policy/_types/manuals/draft';
import StatusChip from './StatusChip';
import parse from 'html-react-parser';
import ExcalidrawImage from 'views/App/Portal/Policy/shared/Excalidraw';

interface ReleaseNotesDrawerProps {
  releaseNotes?: ReleaseNotes | null;
  manual?: DraftManual | null;
  onNoteIndexChange: (addNumber: number) => void;
  currentNoteIndex: number;
  isLoading?: boolean;
  isEditing?: boolean;
  handleApproveReleaseNotes?: () => Promise<void>;
  emptyMessage?: string;
}

const ReleaseNotesDrawer = ({
  releaseNotes,
  manual,
  onNoteIndexChange,
  handleApproveReleaseNotes,
  currentNoteIndex,
  isLoading,
  isEditing,
  emptyMessage,
}: ReleaseNotesDrawerProps) => {
  const extractExcalidrawData = (htmlContent: string): string[] => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    const excalidrawElements = doc.querySelectorAll('[data-lexical-excalidraw-json]');

    const excalidrawDataArray: string[] = [];
    excalidrawElements.forEach((element) => {
      const data = element.getAttribute('data-lexical-excalidraw-json');
      if (data) {
        excalidrawDataArray.push(data);
      }
    });

    return excalidrawDataArray;
  };

  const renderContentWithExcalidraw = (htmlContent: string) => {
    const excalidrawDataArray = extractExcalidrawData(htmlContent);

    if (excalidrawDataArray.length === 0) {
      return parse(htmlContent);
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    const excalidrawElements = doc.querySelectorAll('[data-lexical-excalidraw-json]');

    excalidrawElements.forEach((element, index) => {
      const placeholder = doc.createElement('div');
      placeholder.setAttribute('data-excalidraw-placeholder', index.toString());
      element.parentNode?.replaceChild(placeholder, element);
    });

    const parsedContent = parse(doc.body.innerHTML, {
      replace: (domNode: any) => {
        if (domNode.attribs && domNode.attribs['data-excalidraw-placeholder'] !== undefined) {
          const index = parseInt(domNode.attribs['data-excalidraw-placeholder']);
          const excalidrawData = excalidrawDataArray[index];

          if (excalidrawData) {
            return (
              <ExcalidrawImage
                key={`excalidraw-${index}`}
                excalidrawJson={excalidrawData}
                alt='Excalidraw diagram'
              />
            );
          }
        }
      },
    });

    return parsedContent;
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!releaseNotes) {
    return <Box sx={{ p: 2, whiteSpace: 'normal', wordBreak: 'break-word' }}>{emptyMessage ?? t('draftManuals.releaseNotes.noReleaseNotes')}</Box>;
  }

  const currentNote = releaseNotes.history?.[currentNoteIndex];
  const currentNotesStatus = releaseNotes.draftState;

  return (
    <Box
      component='section'
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', p: 2, gap: 2 }}
    >
      <Box sx={{ flexBasis: '95%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography
            variant='bodySm'
            sx={{ flexGrow: 1 }}
          >
            {currentNote.title}
          </Typography>
          <StatusChip
            draftState={currentNotesStatus}
            manualLevel={manual?.manualLevel}
            isEditing={isEditing}
            onApproveReleaseNotes={handleApproveReleaseNotes}
          />
        </Box>
        <Box sx={{ display: 'flex', overflow: 'hidden' }}>
          <Box
            sx={{
              '& *': {
                maxWidth: '100%',
                minWidth: 0,
                boxSizing: 'border-box',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
                whiteSpace: 'normal !important',
              },
              '& p': { margin: 0, whiteSpace: 'normal' },
              '& pre, & code': { whiteSpace: 'pre-wrap' },
              '& img, & video': { maxWidth: '100%', height: 'auto' },
              '& table': { display: 'block', width: '100%', tableLayout: 'fixed', overflowX: 'auto' },
              '& ul, & ol': { margin: 0, paddingLeft: '1.5rem', maxWidth: '100%' },
              '& li': { whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'anywhere' },
              '& .PhxEditorTheme__paragraph': {
                maxWidth: '100%',
                whiteSpace: 'normal',
                position: 'static',
                left: 'auto',
                right: 'auto',
              },
            }}
          >
            {renderContentWithExcalidraw(currentNote.content || '')}
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          flexBasis: '5%',
          display: 'flex',
          alignContent: 'space-around',
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <IconButton
            disabled={currentNoteIndex === 0}
            onClick={() => onNoteIndexChange(-1)}
          >
            <ArrowBackIcon />
          </IconButton>
        </Box>
        <Box>
          <Typography variant='bodySm'>{`${currentNoteIndex + 1} ${t('draftManuals.releaseNotes.of')} ${releaseNotes.history.length}`}</Typography>
        </Box>
        <Box>
          <IconButton
            disabled={currentNoteIndex === releaseNotes.history.length - 1}
            onClick={() => onNoteIndexChange(1)}
          >
            <ArrowForwardIcon />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default ReleaseNotesDrawer;
